import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ChartSpec, ChartType } from 'src/app/services/copilot/copilot.types';

interface BarRect {
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
  value: number;
  /** Gateway-formatted display value ("7h 32m") aligned with the prose. */
  formatted?: string;
  color: string;
}

interface LinePoint {
  x: number;
  y: number;
}

interface PieSlice {
  path: string;
  color: string;
  label: string;
  value: number;
  percent: number;
}

interface CategoryLabel {
  x: number;
  y: number;
  text: string;
  full: string;
  rotate: boolean;
}

/** Two-person comparisons otherwise render as slabs filling half the canvas. */
const MAX_BAR_WIDTH = 72;

const PALETTE = [
  '#0166ff',
  '#03b453',
  '#f5a623',
  '#7b61ff',
  '#e5484d',
  '#0d9488',
  '#db2777',
  '#64748b',
  '#0ea5e9',
  '#a855f7',
];

@Component({
  selector: 'app-copilot-chart',
  templateUrl: './copilot-chart.component.html',
  styleUrls: ['./copilot-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CopilotChartComponent {
  @Input() chart: ChartSpec | null | undefined;
  /** SVG logical width — dialog uses a wider canvas for readability. */
  @Input() width = 320;
  /** Inline chat card vs expanded dialog (taller plot band). */
  @Input() variant: 'inline' | 'dialog' = 'inline';

  get type(): ChartType | null {
    return this.chart?.type ?? null;
  }

  get isCartesian(): boolean {
    const t = this.type;
    return t === 'bar' || t === 'stacked_bar' || t === 'multi_series_bar' || t === 'line' || t === 'multi_series_line';
  }

  get isPie(): boolean {
    return this.type === 'pie' || this.type === 'donut';
  }

  /** Single-series ranking bars get per-category colors + optional legend. */
  get isCategoryBar(): boolean {
    const c = this.chart;
    return !!c && (c.type === 'bar' || c.type === 'multi_series_bar') && c.series.length === 1;
  }

  /**
   * Person / ranking bars: always use a color legend with full names.
   * Rotated axis labels truncate badly in the narrow chat card.
   */
  get categoryLabelMode(): 'axis' | 'rotated' | 'legend' {
    const c = this.chart;
    if (!c || !this.isCartesian) {
      return 'axis';
    }
    if (this.isCategoryBar) {
      return 'legend';
    }
    const n = c.categories.length;
    const longest = c.categories.reduce((m, t) => Math.max(m, t.length), 0);
    if (n >= 5 || longest > 12) {
      return 'legend';
    }
    if (n >= 4 || longest > 10) {
      return 'rotated';
    }
    return 'axis';
  }

  get pad(): { top: number, right: number, bottom: number, left: number } {
    const bottom =
      this.categoryLabelMode === 'rotated' ? 58 : this.categoryLabelMode === 'legend' ? 16 : 28;
    // Headroom so the tallest bar's caption is not painted over the bar.
    return { top: this.showsBarValues ? 26 : 12, right: 12, bottom, left: 40 };
  }

  /**
   * Value captions are an expanded-view affordance, and only readable while
   * the category count is low enough that captions cannot collide.
   */
  get showsBarValues(): boolean {
    const c = this.chart;
    return (
      this.variant === 'dialog' &&
      !!c &&
      this.isCartesian &&
      c.series.length === 1 &&
      c.categories.length <= 8
    );
  }

  /** Plot band height — compact in chat, taller in the expand dialog. */
  get plotBand(): number {
    return this.variant === 'dialog' ? 300 : 120;
  }

  get height(): number {
    return this.pad.top + this.plotBand + this.pad.bottom;
  }

  get plotWidth(): number {
    return this.width - this.pad.left - this.pad.right;
  }

  get plotHeight(): number {
    return this.height - this.pad.top - this.pad.bottom;
  }

  get maxValue(): number {
    const c = this.chart;
    if (!c?.series?.length) {
      return 1;
    }
    if (c.type === 'stacked_bar') {
      const n = c.categories.length;
      let stackedMax = 0;
      for (let i = 0; i < n; i++) {
        let sum = 0;
        for (const s of c.series) {
          sum += Math.max(0, s.values[i] ?? 0);
        }
        stackedMax = Math.max(stackedMax, sum);
      }
      return Math.max(stackedMax, c.referenceLine?.value ?? 0) || 1;
    }
    let seriesMax = 0;
    for (const s of c.series) {
      for (const v of s.values) {
        seriesMax = Math.max(seriesMax, v);
      }
    }
    // The reference line (goal/baseline) must stay inside the plot.
    const ref = c.referenceLine?.value ?? 0;
    return Math.max(seriesMax, ref) || 1;
  }

  /** Horizontal goal/baseline marker (cartesian charts only). */
  get referenceLineY(): number | null {
    const c = this.chart;
    if (!c?.referenceLine || !this.isCartesian) {
      return null;
    }
    const v = Math.max(0, c.referenceLine.value);
    return this.pad.top + this.plotHeight - (v / this.maxValue) * this.plotHeight;
  }

  get barRects(): BarRect[] {
    const c = this.chart;
    if (!c || (c.type !== 'bar' && c.type !== 'multi_series_bar')) {
      return [];
    }
    const cats = c.categories;
    const series = c.series;
    const groupW = this.plotWidth / Math.max(cats.length, 1);
    const barW = Math.max(
      4,
      Math.min(MAX_BAR_WIDTH, (groupW * 0.7) / Math.max(series.length, 1)),
    );
    const max = this.maxValue;
    const rects: BarRect[] = [];
    cats.forEach((cat, i) => {
      series.forEach((s, si) => {
        const v = Math.max(0, s.values[i] ?? 0);
        const h = (v / max) * this.plotHeight;
        const x =
          this.pad.left +
          i * groupW +
          (groupW - barW * series.length) / 2 +
          si * barW;
        const y = this.pad.top + this.plotHeight - h;
        const color =
          series.length === 1 ? PALETTE[i % PALETTE.length] : PALETTE[si % PALETTE.length];
        rects.push({
          x,
          y,
          width: barW - 1,
          height: h,
          label: series.length > 1 ? `${cat} · ${s.label}` : cat,
          value: v,
          formatted: s.formattedValues?.[i],
          color,
        });
      });
    });
    return rects;
  }

  get barValueLabels(): Array<{ x: number, y: number, text: string }> {
    if (!this.showsBarValues) {
      return [];
    }
    return this.barRects
      .filter((bar) => bar.height > 0)
      .map((bar) => ({
        x: bar.x + bar.width / 2,
        y: bar.y - 7,
        text: bar.formatted ?? this.valueWithUnit(bar.value),
      }));
  }

  get stackedRects(): BarRect[] {
    const c = this.chart;
    if (!c || c.type !== 'stacked_bar') {
      return [];
    }
    const cats = c.categories;
    const series = c.series;
    const groupW = this.plotWidth / Math.max(cats.length, 1);
    const barW = Math.max(6, Math.min(MAX_BAR_WIDTH, groupW * 0.55));
    const max = this.maxValue;
    const rects: BarRect[] = [];
    cats.forEach((cat, i) => {
      let yBase = this.pad.top + this.plotHeight;
      series.forEach((s, si) => {
        const v = Math.max(0, s.values[i] ?? 0);
        const h = (v / max) * this.plotHeight;
        yBase -= h;
        rects.push({
          x: this.pad.left + i * groupW + (groupW - barW) / 2,
          y: yBase,
          width: barW,
          height: h,
          label: `${cat} · ${s.label}`,
          value: v,
          formatted: s.formattedValues?.[i],
          color: PALETTE[si % PALETTE.length],
        });
      });
    });
    return rects;
  }

  get linePaths(): Array<{ d: string, color: string, label: string, points: LinePoint[] }> {
    const c = this.chart;
    if (!c || (c.type !== 'line' && c.type !== 'multi_series_line')) {
      return [];
    }
    const cats = c.categories;
    const max = this.maxValue;
    const n = Math.max(cats.length - 1, 1);
    return c.series.map((s, si) => {
      const points = s.values.map((v, i) => ({
        x: this.pad.left + (i / n) * this.plotWidth,
        y: this.pad.top + this.plotHeight - (Math.max(0, v) / max) * this.plotHeight,
      }));
      const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
      return {
        d,
        color: PALETTE[si % PALETTE.length],
        label: s.label,
        points,
      };
    });
  }

  get pieSlices(): PieSlice[] {
    const c = this.chart;
    if (!c || !this.isPie) {
      return [];
    }
    const values = c.categories.map((_, i) =>
      c.series.reduce((sum, s) => sum + Math.max(0, s.values[i] ?? 0), 0),
    );
    const total = values.reduce((a, b) => a + b, 0) || 1;
    const cx = this.width / 2;
    const cy = this.height / 2 - 4;
    const r = Math.min(this.width, this.height) / 2 - 28;
    const inner = c.type === 'donut' ? r * 0.55 : 0;
    let angle = -Math.PI / 2;
    return values.map((v, i) => {
      const sweep = (v / total) * Math.PI * 2;
      const path = this.describeArc(cx, cy, r, inner, angle, angle + sweep);
      const slice: PieSlice = {
        path,
        color: PALETTE[i % PALETTE.length],
        label: c.categories[i],
        value: v,
        percent: (v / total) * 100,
      };
      angle += sweep;
      return slice;
    });
  }

  get categoryLabels(): CategoryLabel[] {
    const c = this.chart;
    if (!c || !this.isCartesian || this.categoryLabelMode === 'legend') {
      return [];
    }
    const n = c.categories.length;
    const groupW = this.plotWidth / Math.max(n, 1);
    const rotate = this.categoryLabelMode === 'rotated';
    const maxChars = rotate ? 12 : Math.max(4, Math.floor(groupW / 6));
    return c.categories.map((full, i) => ({
      x: this.pad.left + i * groupW + groupW / 2,
      y: this.height - (rotate ? 8 : 10),
      text: this.shortenLabel(full, maxChars),
      full,
      rotate,
    }));
  }

  get yTicks(): Array<{ y: number, label: string }> {
    if (!this.isCartesian) {
      return [];
    }
    const max = this.maxValue;
    // The taller dialog canvas can carry quarter gridlines without crowding.
    const steps = this.variant === 'dialog' ? [0, 0.25, 0.5, 0.75, 1] : [0, 0.5, 1];
    return steps.map((t) => {
      const v = max * t;
      return {
        y: this.pad.top + this.plotHeight * (1 - t),
        label: this.formatValue(v),
      };
    });
  }

  /** Baseline drawn darker than the gridlines so bars sit on a real axis. */
  get baselineY(): number {
    return this.pad.top + this.plotHeight;
  }

  get legendItems(): Array<{ color: string, label: string, value?: string }> {
    const c = this.chart;
    if (!c) {
      return [];
    }
    if (this.isPie) {
      return c.categories.map((label, i) => ({
        color: PALETTE[i % PALETTE.length],
        label,
      }));
    }
    if (this.categoryLabelMode === 'legend' && this.isCategoryBar) {
      const values = c.series[0]?.values ?? [];
      const formatted = c.series[0]?.formattedValues;
      return c.categories.map((label, i) => ({
        color: PALETTE[i % PALETTE.length],
        label,
        // Prefer the gateway-formatted value — it matches the prose exactly.
        value: formatted?.[i] ?? this.valueWithUnit(values[i] ?? 0),
      }));
    }
    if (c.series.length <= 1) {
      return [];
    }
    return c.series.map((s, i) => ({
      color: PALETTE[i % PALETTE.length],
      label: s.label,
    }));
  }

  formatValue(v: number): string {
    const rounded = Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 10) / 10;
    return String(rounded);
  }

  /**
   * Legend/label text carries its own unit; gateway-formatted values such as
   * "39h 15m" already read as a duration and must not gain a unit suffix.
   */
  private valueWithUnit(v: number): string {
    const unit = this.chart?.unit;
    const value = this.formatValue(v);
    return unit ? `${value} ${unit}` : value;
  }

  /** Hover text for truncated legend / axis labels. */
  tooltipFor(item: { label: string, value?: string }): string {
    return item.value != null ? `${item.label}: ${item.value}` : item.label;
  }

  barTooltip(bar: BarRect): string {
    if (bar.formatted) {
      return `${bar.label}: ${bar.formatted}`;
    }
    const unit = this.chart?.unit;
    const value = this.formatValue(bar.value);
    return unit ? `${bar.label}: ${value} ${unit}` : `${bar.label}: ${value}`;
  }

  private shortenLabel(text: string, maxChars: number): string {
    const t = text.trim();
    if (t.length <= maxChars) {
      return t;
    }
    // Prefer first token when names are "First Last"
    const first = t.split(/\s+/)[0] ?? t;
    if (first.length <= maxChars) {
      return first;
    }
    return `${t.slice(0, Math.max(1, maxChars - 1))}…`;
  }

  private describeArc(
    cx: number,
    cy: number,
    r: number,
    inner: number,
    start: number,
    end: number,
  ): string {
    const large = end - start > Math.PI ? 1 : 0;
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    if (inner <= 0) {
      return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
    }
    const ix1 = cx + inner * Math.cos(end);
    const iy1 = cy + inner * Math.sin(end);
    const ix2 = cx + inner * Math.cos(start);
    const iy2 = cy + inner * Math.sin(start);
    return [
      `M ${x1} ${y1}`,
      `A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`,
      `L ${ix1} ${iy1}`,
      `A ${inner} ${inner} 0 ${large} 0 ${ix2} ${iy2}`,
      'Z',
    ].join(' ');
  }
}
